import { CircularProgress } from '@material-ui/core';
import React, { useCallback, useEffect } from 'react';
import { Button } from 'components/Button/Button';
import { CloseIconButton } from 'components/Button/CloseButton/CloseIconButton';
import { SparkleIcon } from 'components/Icons';
import { TextArea } from 'components/TextArea/TextArea';
import { Text } from 'components/Typography';
import { Color } from 'utils/color';
import { MAX_TASK_MESSAGE_LENGTH } from '../../configConsts';
import style from './GeneratingMessageTextArea.scss';

export type GeneratingMessageTextAreaProps = {
  message: string;
  setMessage: (message: string) => void;
  createMessage: () => void;
  generatedMessage: Nullable<string>;
  loadingMessage: boolean;
  clearGeneratedMessage: () => void;
  editGeneratedMessage: (newMessage: string) => void;
  autoRequest?: boolean;
  title?: string;
};

export const GeneratingMessageTextArea: React.FC<GeneratingMessageTextAreaProps> = ({
  message,
  setMessage,
  createMessage,
  loadingMessage,
  generatedMessage,
  clearGeneratedMessage,
  editGeneratedMessage,
  autoRequest = false,
  title = ''
}) => {
  useEffect(() => {
    if (!generatedMessage && autoRequest) {
      createMessage();
    }
  }, []);

  const internalInsertGeneratedMessage = useCallback(() => {
    if (generatedMessage) {
      setMessage(generatedMessage);
      clearGeneratedMessage();
    }
  }, [generatedMessage, clearGeneratedMessage, setMessage]);

  return (
    <div className={style.container}>
      {!!title && (
        <Text variant="sm" className={style.title}>
          {title}
        </Text>
      )}
      <TextArea
        // multiline
        rows={4}
        // variant='outlined'
        placeholder="Type message... (ie. Please make sure to do your exercises today.)"
        // fullWidth
        value={message}
        maxLength={MAX_TASK_MESSAGE_LENGTH}
        onChange={(e) => setMessage(e.target.value)}
        enclosedContent={
          <>
            {!(loadingMessage || generatedMessage) && (
              <Button variant="outlined" disabled={!message} onClick={createMessage}>
                <Text className={!message ? style.generateButtonTextDisabled : style.generateButtonText}>Generate</Text>
                <SparkleIcon height={16} width={16} color={!message ? Color.GRAY_400 : Color.DARK_BLUE_NEW}></SparkleIcon>
              </Button>
            )}
            {loadingMessage && !generatedMessage && (
              <div className={style.generatingMessageSpinnerContainer}>
                <CircularProgress color="inherit" />
                <Text>Generating...</Text>
              </div>
            )}
            {generatedMessage && (
              <div className={style.generatedMessageContainer}>
                <div className={style.generatedMessageHeader}>
                  <SparkleIcon height={16} width={16} color={Color.LIGHT_BLUE_800}></SparkleIcon>
                  <Text variant="md" weight={400} className={style.reviewMessageTitleText}>
                    Review message
                  </Text>
                  <CloseIconButton containerClassName={style.generatedMessageCloseButton} onClick={clearGeneratedMessage}></CloseIconButton>
                </div>
                <TextArea
                  value={generatedMessage}
                  onChange={(e) => {
                    editGeneratedMessage(e.target.value);
                  }}
                  className={style.generatedMessageTextArea}
                  isTransparent
                  autoResize
                  rows={1}
                ></TextArea>
                <div className={style.generatedMessageFooter}>
                  <Button variant="outlined" disabled={!message} onClick={createMessage}>
                    <Text className={!message ? style.generateButtonTextDisabled : style.generateButtonText}>Regenerate</Text>
                    <SparkleIcon height={16} width={16} color={!message ? Color.GRAY_400 : Color.DARK_BLUE_NEW}></SparkleIcon>
                  </Button>
                  <Button variant="contained" onClick={internalInsertGeneratedMessage}>
                    Insert
                  </Button>
                </div>
              </div>
            )}
          </>
        }
      />
    </div>
  );
};
